import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WishlistContext = createContext();

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
};

export const WishlistProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [productIds, setProductIds] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setProductIds([]);
      setProducts([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProductIds(res.data.product_ids || []);
      setProducts(res.data.products || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isInWishlist = (productId) => productIds.includes(productId);

  const toggle = async (productId) => {
    if (!isAuthenticated || !token) return { ok: false, reason: 'auth' };
    try {
      if (productIds.includes(productId)) {
        await axios.delete(`${API}/wishlist/${productId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProductIds((prev) => prev.filter((id) => id !== productId));
        setProducts((prev) => prev.filter((p) => p.id !== productId));
        return { ok: true, action: 'removed' };
      }
      await axios.post(`${API}/wishlist/${productId}`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProductIds((prev) => [...prev, productId]);
      // Optimistically refresh full list
      refresh();
      return { ok: true, action: 'added' };
    } catch (e) {
      return { ok: false, error: e };
    }
  };

  return (
    <WishlistContext.Provider value={{ productIds, products, loading, isInWishlist, toggle, refresh }}>
      {children}
    </WishlistContext.Provider>
  );
};
